import 'mapbox-gl/dist/mapbox-gl.css';

import { assertSome, useForwardedRef } from '@crocodille/common';
import { MapBoxProvider, useMapbox, useMapboxEffect } from '@hooks/use-mapbox';
import cn from 'classnames';
import React, { forwardRef } from 'react';

import styles from './map-box.module.scss';
import type { LngLatLike } from 'mapbox-gl';

export type MapBoxProps = React.HTMLAttributes<HTMLDivElement> & {
  readonly mapCenter?: LngLatLike;
  readonly mapZoom?: number;
  readonly mapMaxZoom?: number;
  readonly fill?: boolean;
};

const DEFAULT_CENTER = [14.3968, 50.1001] satisfies LngLatLike;
const DEFAULT_ZOOM = 10;
const DEFAULT_MAX_ZOOM = 16;

const MapBoxInner: React.FC<
  Pick<MapBoxProps, 'mapCenter' | 'mapZoom' | 'mapMaxZoom'>
> = ({
  mapCenter = DEFAULT_CENTER,
  mapZoom = DEFAULT_ZOOM,
  mapMaxZoom = DEFAULT_MAX_ZOOM,
}) => {
  useMapboxEffect(
    (map) => {
      map.setCenter(mapCenter);
    },
    [mapCenter],
  );

  useMapboxEffect(
    (map) => {
      map.setZoom(mapZoom);
    },
    [mapZoom],
  );

  useMapboxEffect(
    (map) => {
      map.setMaxZoom(mapMaxZoom);
    },
    [mapMaxZoom],
  );

  return null;
};

const MapBox = forwardRef(
  (
    {
      className,
      fill,
      children,
      mapCenter = DEFAULT_CENTER,
      mapZoom = DEFAULT_ZOOM,
      mapMaxZoom = DEFAULT_MAX_ZOOM,
      ...props
    }: MapBoxProps,
    outerRef: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [containerRef, ref] = useForwardedRef(outerRef);

    const mapBoxCtx = useMapbox(containerRef, {
      accessToken: assertSome(process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN),
      style: 'mapbox://styles/mapbox/light-v10',
      center: mapCenter,
      zoom: mapZoom,
      maxZoom: mapMaxZoom,
    });

    return (
      <div
        ref={ref}
        className={cn(styles.container, { [styles.isFill]: fill }, className)}
        {...props}
      >
        <MapBoxProvider value={mapBoxCtx}>
          <MapBoxInner mapCenter={mapCenter} mapZoom={mapZoom} />
          {children}
        </MapBoxProvider>
      </div>
    );
  },
);

MapBox.displayName = 'MapBox';

export default MapBox;
